import { useEffect } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from './Button';
import FormInput from './FormInput';
import Modal from './Modal';
import FormDatePicker from './FormDatePicker';
import FormTextarea from './FormTextarea';
import { useTranslation } from 'react-i18next';
import i18n from '../common/config/i18n';
import { IRequest, IRequestDTO } from '../common/interfaces/request.interface';

interface RequestModalProps {
  title: string;
  onClose: () => void;
  onSubmit: (request: IRequestDTO) => void;
  buttonLabel: string;
  request?: IRequest | null;
}

const schema = yup
  .object({
    name: yup.string().required(i18n.t('request_modal:name.errors.required')),
    alias: yup.string().notRequired(),
    cnp: yup.string().required(i18n.t('request_modal:cnp.errors.required')),
    start_date: yup.date().required(i18n.t('request_modal:start_date.errors.required')),
    end_date: yup.date().required(i18n.t('request_modal:end_date.errors.required')),
    birthplace: yup.string().notRequired(),
    working_periods: yup.string().notRequired(),
    job_titles: yup.string().notRequired(),
    details: yup.string().notRequired(),
  })
  .required();

const RequestModal = ({ title, onClose, onSubmit, buttonLabel, request }: RequestModalProps) => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const { t } = useTranslation('request_modal');
  const start_date = watch('start_date');

  useEffect(() => {
    if (request) {
      reset(request);
    }
  }, [request]);

  return (
    <Modal title={title} onClose={onClose} width="sm:max-w-4xl" closeOnlyOnButton={true}>
      <div className="flex flex-col px-12 w-full gap-[60px]">
        <form className="gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex gap-4">
            <div className="flex flex-col gap-4 w-full">
              <Controller
                key="name"
                name="name"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormInput
                      type="text"
                      errorMessage={errors.name?.message?.toString()}
                      value={value}
                      label={t('name.label')}
                      onChange={onChange}
                      helper={t('name.helper')}
                    />
                  );
                }}
              />
              <Controller
                key="alias"
                name="alias"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormInput
                      type="text"
                      errorMessage={errors.alias?.message?.toString()}
                      value={value}
                      label={t('alias.label')}
                      onChange={onChange}
                      helper={t('alias.helper')}
                    />
                  );
                }}
              />
              <Controller
                key="cnp"
                name="cnp"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormInput
                      type="text"
                      errorMessage={errors.cnp?.message?.toString()}
                      value={value}
                      label={t('cnp.label')}
                      onChange={onChange}
                      helper={t('cnp.helper')}
                    />
                  );
                }}
              />
              <div className="flex gap-4">
                <Controller
                  key="start_date"
                  name="start_date"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FormDatePicker
                        name="end_date"
                        label={t('start_date.label')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors.start_date?.message?.toString()}
                        maxDate={undefined}
                        helper={t('start_date.helper')}
                      />
                    );
                  }}
                />
                <Controller
                  key="end_date"
                  name="end_date"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FormDatePicker
                        name="end_date"
                        label={t('end_date.label')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors.end_date?.message?.toString()}
                        minDate={start_date}
                        helper={t('end_date.helper')}
                      />
                    );
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 w-full">
              <Controller
                key="birthplace"
                name="birthplace"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormInput
                      type="text"
                      errorMessage={errors.birthplace?.message?.toString()}
                      value={value}
                      label={t('birthplace.label')}
                      onChange={onChange}
                      helper={t('birthplace.helper')}
                    />
                  );
                }}
              />
              <Controller
                key="working_periods"
                name="working_periods"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormInput
                      type="text"
                      errorMessage={errors.working_periods?.message?.toString()}
                      value={value}
                      label={t('working_periods.label')}
                      onChange={onChange}
                      helper={t('working_periods.helper')}
                    />
                  );
                }}
              />
              <Controller
                key="job_titles"
                name="job_titles"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormInput
                      type="text"
                      errorMessage={errors.job_titles?.message?.toString()}
                      value={value}
                      label={t('job_titles.label')}
                      onChange={onChange}
                      helper={t('job_titles.helper')}
                    />
                  );
                }}
              />
              <Controller
                key="details"
                name="details"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <FormTextarea
                      readOnly={false}
                      value={value}
                      label={`Observatii`}
                      onChange={onChange}
                      helper={t('details.helper')}
                    />
                  );
                }}
              />
            </div>
          </div>
        </form>
        <div className="flex flex-row-reverse">
          <Button
            id="project_form__submit-button"
            label={buttonLabel}
            className="btn-primary w-full"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RequestModal;
